import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Pencil, Trash2, Copy, Send, FileText, ListChecks, Blocks, Upload,
  Users, Download, FilePlus2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import {
  ContentBlocksEditor, DocContentView, StatusBadge, STATUS_META, downloadApiFile,
} from "@/components/shared/DocKit";

const ROLES = ["berater", "trainee", "manager", "backoffice", "compliance", "admin"];
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`);

function catLabel(meta, id) {
  return meta.categories.find((c) => c.id === id)?.label || id;
}

/* =============== Templates tab =============== */
function TemplatesTab({ meta }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const load = useCallback(() => { api.get("/docs/templates").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(load, [load]);

  const duplicate = async (id) => {
    const res = await api.post(`/docs/templates/${id}/duplicate`);
    toast.success(t("Vorlage dupliziert", "Template duplicated"));
    navigate(`/admin/dokumente/vorlagen/${res.data.id}`);
  };
  const publish = async (id) => {
    try {
      await api.post(`/docs/templates/${id}/publish`);
      toast.success(t("Ver\u00f6ffentlicht", "Published"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("Vorlage wirklich l\u00f6schen?", "Really delete template?"))) return;
    await api.delete(`/docs/templates/${id}`);
    toast.success(t("Vorlage gel\u00f6scht", "Template deleted"));
    load();
  };

  if (!items) return <Skeleton className="h-64 rounded-[16px]" />;
  return (
    <div className="space-y-3" data-testid="templates-tab">
      <div className="flex justify-end">
        <Button className="rounded-[10px] bg-[#0B1220]" onClick={() => navigate("/admin/dokumente/vorlagen/neu")} data-testid="template-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> {t("Neue Vorlage", "New template")}
        </Button>
      </div>
      {items.map((tp) => (
        <Card key={tp.id} className="rounded-[14px]" data-testid={`template-row-${tp.id}`}>
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{tp.name}</p>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={tp.status} />
                <Badge variant="secondary" className="rounded-full text-[10px]">v{tp.version || 0}</Badge>
                <span className="text-[11px] text-muted-foreground">{catLabel(meta, tp.category)}</span>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {tp.status !== "published" && (
                <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => publish(tp.id)} title={t("Ver\u00f6ffentlichen", "Publish")} data-testid={`template-publish-${tp.id}`}><Send className="h-3.5 w-3.5" /></Button>
              )}
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => duplicate(tp.id)} title={t("Duplizieren", "Duplicate")} data-testid={`template-duplicate-${tp.id}`}><Copy className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => navigate(`/admin/dokumente/vorlagen/${tp.id}`)} data-testid={`template-edit-${tp.id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1" /> {t("Bearbeiten", "Edit")}
              </Button>
              <Button size="sm" variant="outline" className="rounded-[10px] text-destructive" onClick={() => remove(tp.id)} data-testid={`template-delete-${tp.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* =============== Text blocks tab =============== */
function BlocksTab({ meta }) {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get("/docs/blocks").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!edit.title?.trim() || !edit.content?.trim()) return toast.error(t("Titel und Inhalt erforderlich", "Title and content required"));
    const body = { title: edit.title, category: edit.category, content: edit.content, required: !!edit.required };
    try {
      if (edit.id) await api.patch(`/docs/blocks/${edit.id}`, body);
      else await api.post("/docs/blocks", body);
      toast.success(t("Textbaustein gespeichert", "Text block saved"));
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const remove = async (id) => {
    await api.delete(`/docs/blocks/${id}`);
    toast.success(t("Gel\u00f6scht", "Deleted"));
    load();
  };

  if (!items) return <Skeleton className="h-64 rounded-[16px]" />;
  const blockCatLabel = (id) => meta.block_categories.find((c) => c.id === id)?.label || id;
  return (
    <div className="space-y-3" data-testid="blocks-tab">
      <div className="flex justify-end">
        <Button className="rounded-[10px] bg-[#0B1220]" onClick={() => setEdit({ title: "", category: meta.block_categories[0]?.id || "compliance", content: "", required: false })} data-testid="block-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> {t("Neuer Textbaustein", "New text block")}
        </Button>
      </div>
      {items.map((b) => (
        <Card key={b.id} className="rounded-[14px]" data-testid={`block-row-${b.id}`}>
          <CardContent className="p-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold flex items-center gap-2">
                {b.title}
                {b.required && <Badge variant="outline" className="rounded-full text-[9px] border-[#D6C08D]/50 text-[#A8905A]">Pflicht</Badge>}
              </p>
              <p className="text-[11px] text-muted-foreground">{blockCatLabel(b.category)}</p>
              <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{b.content}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => setEdit({ ...b })} data-testid={`block-edit-${b.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-[10px] text-destructive" onClick={() => remove(b.id)} data-testid={`block-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{edit?.id ? t("Textbaustein bearbeiten", "Edit text block") : t("Neuer Textbaustein", "New text block")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>{t("Titel", "Title")}</Label>
                <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="rounded-[10px]" data-testid="block-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("Kategorie", "Category")}</Label>
                  <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                    <SelectTrigger className="rounded-[10px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.block_categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <label className="flex items-center gap-2.5 text-sm pt-6">
                  <Switch checked={!!edit.required} onCheckedChange={(v) => setEdit({ ...edit, required: v })} /> {t("Pflichtbaustein", "Mandatory block")}
                </label>
              </div>
              <div className="space-y-1.5"><Label>{t("Inhalt", "Content")}</Label>
                <Textarea rows={5} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} className="rounded-[10px]" data-testid="block-content-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button className="rounded-[10px] bg-[#0B1220]" onClick={save} data-testid="block-save-button">{t("Speichern", "Save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =============== Managed documents tab =============== */
function DocumentsTab({ meta }) {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [acks, setAcks] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => { api.get("/docs/documents").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(load, [load]);

  const emptyDoc = { title: "", category: "compliance", description: "", file_id: null, file_name: "", content: null, requires_acknowledgement: false, assigned_roles: [], assigned_users: [], status: "draft", context_type: null, context_id: null };

  const openEdit = async (d) => {
    const full = await api.get(`/docs/documents/${d.id}`);
    setEdit({ ...full.data });
  };

  const save = async () => {
    if (!edit.title?.trim()) return toast.error(t("Titel erforderlich", "Title required"));
    const body = {
      title: edit.title, category: edit.category, description: edit.description || "",
      file_id: edit.file_id || null, content: edit.content || null,
      requires_acknowledgement: !!edit.requires_acknowledgement,
      assigned_roles: edit.assigned_roles || [], assigned_users: edit.assigned_users || [],
      context_type: edit.context_type || null, context_id: edit.context_id || null,
      status: edit.status,
    };
    try {
      if (edit.id) await api.patch(`/docs/documents/${edit.id}`, body);
      else await api.post("/docs/documents", body);
      toast.success(t("Dokument gespeichert", "Document saved"));
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const newVersion = async (d) => {
    const note = window.prompt(t("Notiz zur neuen Version (optional):", "Note for the new version (optional):")) || "";
    try {
      const res = await api.post(`/docs/documents/${d.id}/new-version`, { note });
      toast.success(t(`Version ${res.data.version} erstellt \u2013 Best\u00e4tigungen werden erneut f\u00e4llig`, `Version ${res.data.version} created \u2013 acknowledgements required again`));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("Dokument wirklich l\u00f6schen?", "Really delete document?"))) return;
    await api.delete(`/docs/documents/${id}`);
    toast.success(t("Gel\u00f6scht", "Deleted"));
    load();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("area", "documents");
    try {
      setUploading(true);
      const res = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((d) => ({ ...d, file_id: res.data.id, file_name: res.data.filename }));
      toast.success(t("Datei hochgeladen", "File uploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("Upload fehlgeschlagen", "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const showAcks = async (d) => {
    const res = await api.get(`/docs/documents/${d.id}/acknowledgements`);
    setAcks({ doc: d, list: res.data });
  };
  const showVersions = async (d) => {
    const res = await api.get(`/docs/documents/${d.id}/versions`);
    setVersionsFor({ doc: d, list: res.data });
  };

  if (!items) return <Skeleton className="h-64 rounded-[16px]" />;
  return (
    <div className="space-y-3" data-testid="documents-tab">
      <div className="flex justify-end">
        <Button className="rounded-[10px] bg-[#0B1220]" onClick={() => setEdit({ ...emptyDoc })} data-testid="document-create-button">
          <FilePlus2 className="h-4 w-4 mr-1.5" /> {t("Neues Dokument", "New document")}
        </Button>
      </div>
      {items.map((d) => (
        <Card key={d.id} className="rounded-[14px]" data-testid={`document-row-${d.id}`}>
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{d.title}</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <StatusBadge status={d.status} />
                <Badge variant="secondary" className="rounded-full text-[10px]">v{d.version}</Badge>
                {d.requires_acknowledgement && <Badge variant="outline" className="rounded-full text-[10px] border-[#D6C08D]/50 text-[#A8905A]">{t("Best\u00e4tigungspflichtig", "Ack required")}</Badge>}
                {d.file_id && <Badge variant="outline" className="rounded-full text-[10px]">{t("Datei", "File")}</Badge>}
                <span className="text-[11px] text-muted-foreground">{catLabel(meta, d.category)}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {d.requires_acknowledgement && (
                <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => showAcks(d)} title={t("Best\u00e4tigungen", "Acknowledgements")} data-testid={`document-acks-${d.id}`}><Users className="h-3.5 w-3.5" /></Button>
              )}
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => showVersions(d)} title={t("Versionen", "Versions")} data-testid={`document-versions-${d.id}`}>v</Button>
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => newVersion(d)} data-testid={`document-newversion-${d.id}`}>{t("Neue Version", "New version")}</Button>
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => openEdit(d)} data-testid={`document-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-[10px] text-destructive" onClick={() => remove(d.id)} data-testid={`document-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{edit?.id ? t("Dokument bearbeiten", "Edit document") : t("Neues Dokument", "New document")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5 sm:col-span-2"><Label>{t("Titel", "Title")}</Label>
                  <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="rounded-[10px]" data-testid="document-title-input" /></div>
                <div className="space-y-1.5"><Label>{t("Kategorie", "Category")}</Label>
                  <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                    <SelectTrigger className="rounded-[10px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div className="space-y-1.5"><Label>Status</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                    <SelectTrigger className="rounded-[10px]" data-testid="document-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{t(STATUS_META[s].label, STATUS_META[s].en)}</SelectItem>)}</SelectContent>
                  </Select></div>
                <label className="flex items-center gap-2.5 text-sm pb-2">
                  <Switch checked={!!edit.requires_acknowledgement} onCheckedChange={(v) => setEdit({ ...edit, requires_acknowledgement: v })} data-testid="document-ack-switch" />
                  {t("Best\u00e4tigungspflichtig", "Requires acknowledgement")}
                </label>
                <div className="flex items-center gap-2 pb-1">
                  <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} />
                  <Button type="button" variant="outline" size="sm" className="rounded-[10px]" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="document-upload-button">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {edit.file_id ? t("Datei ersetzen", "Replace file") : t("Datei hochladen", "Upload file")}
                  </Button>
                  {edit.file_name && <span className="text-xs text-muted-foreground truncate max-w-[140px]">{edit.file_name}</span>}
                </div>
              </div>
              <div className="space-y-1.5"><Label>{t("Beschreibung", "Description")}</Label>
                <Textarea rows={2} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="rounded-[10px]" /></div>
              <div className="space-y-1.5">
                <Label>{t("Rollen-Zuweisung (leer = alle)", "Role assignment (empty = all)")}</Label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((r) => {
                    const active = (edit.assigned_roles || []).includes(r);
                    return (
                      <button key={r} type="button" onClick={() => setEdit({ ...edit, assigned_roles: active ? edit.assigned_roles.filter((x) => x !== r) : [...(edit.assigned_roles || []), r] })}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? "bg-[#0B1220] text-white border-transparent" : "border-border hover:bg-muted"}`}>
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("Strukturierter Inhalt (optional, f\u00fcr PDF/Anzeige)", "Structured content (optional, for PDF/display)")}</Label>
                <ContentBlocksEditor content={edit.content || []} onChange={(content) => setEdit({ ...edit, content })} placeholders={meta.placeholders} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button className="rounded-[10px] bg-[#0B1220]" onClick={save} data-testid="document-save-button">{t("Speichern", "Save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* acknowledgements dialog */}
      <Dialog open={!!acks} onOpenChange={(o) => !o && setAcks(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{t("Best\u00e4tigungen", "Acknowledgements")}: {acks?.doc.title}</DialogTitle></DialogHeader>
          {acks?.list.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("Noch keine Best\u00e4tigungen.", "No acknowledgements yet.")}</p>
          ) : (
            <div className="divide-y divide-border max-h-[50vh] overflow-y-auto" data-testid="acks-list">
              {acks?.list.map((a) => (
                <div key={a.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <span>{a.user_name} <span className="text-xs text-muted-foreground">({a.user_role})</span></span>
                  <span className="text-xs text-muted-foreground font-mono-num">v{a.document_version} \u00b7 {new Date(a.acknowledged_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* versions dialog */}
      <Dialog open={!!versionsFor} onOpenChange={(o) => !o && setVersionsFor(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{t("Versionen", "Versions")}: {versionsFor?.doc.title}</DialogTitle></DialogHeader>
          <div className="divide-y divide-border max-h-[50vh] overflow-y-auto" data-testid="versions-list">
            {versionsFor?.list.map((v) => (
              <div key={v.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                <span>Version {v.version}{v.note ? ` \u2013 ${v.note}` : ""}</span>
                <span className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =============== Checklists tab =============== */
function ChecklistsTab({ meta }) {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [subs, setSubs] = useState(null);
  const load = useCallback(() => { api.get("/docs/checklists").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!edit.title?.trim()) return toast.error(t("Titel erforderlich", "Title required"));
    const body = {
      title: edit.title, category: edit.category || "compliance", description: edit.description || "",
      items: (edit.items || []).filter((i) => i.label?.trim()),
      assigned_roles: edit.assigned_roles || [], assigned_users: [], due_days: edit.due_days || null, status: "published",
    };
    try {
      if (edit.id) await api.patch(`/docs/checklists/${edit.id}`, body);
      else await api.post("/docs/checklists", body);
      toast.success(t("Checkliste gespeichert", "Checklist saved"));
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const remove = async (id) => {
    await api.delete(`/docs/checklists/${id}`);
    toast.success(t("Gel\u00f6scht", "Deleted"));
    load();
  };
  const showSubs = async (c) => {
    const res = await api.get(`/docs/checklists/${c.id}/submissions`);
    setSubs({ cl: c, list: res.data });
  };

  if (!items) return <Skeleton className="h-64 rounded-[16px]" />;
  return (
    <div className="space-y-3" data-testid="checklists-tab">
      <div className="flex justify-end">
        <Button className="rounded-[10px] bg-[#0B1220]" onClick={() => setEdit({ title: "", category: "compliance", description: "", items: [], assigned_roles: [], due_days: null })} data-testid="checklist-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> {t("Neue Checkliste", "New checklist")}
        </Button>
      </div>
      {items.map((c) => (
        <Card key={c.id} className="rounded-[14px]" data-testid={`checklist-row-${c.id}`}>
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{c.title}</p>
              <p className="text-[11px] text-muted-foreground">
                {(c.items || []).length} {t("Punkte", "items")}
                {c.assigned_roles?.length > 0 && ` \u00b7 ${c.assigned_roles.join(", ")}`}
                {c.due_days && ` \u00b7 ${t("Frist", "due")}: ${c.due_days} ${t("Tage", "days")}`}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => showSubs(c)} data-testid={`checklist-subs-${c.id}`}><Users className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-[10px]" onClick={() => setEdit(JSON.parse(JSON.stringify(c)))} data-testid={`checklist-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-[10px] text-destructive" onClick={() => remove(c.id)} data-testid={`checklist-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{edit?.id ? t("Checkliste bearbeiten", "Edit checklist") : t("Neue Checkliste", "New checklist")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5 sm:col-span-2"><Label>{t("Titel", "Title")}</Label>
                  <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="rounded-[10px]" data-testid="checklist-title-input" /></div>
                <div className="space-y-1.5"><Label>{t("Frist (Tage)", "Due (days)")}</Label>
                  <Input type="number" value={edit.due_days ?? ""} onChange={(e) => setEdit({ ...edit, due_days: e.target.value ? parseInt(e.target.value) : null })} className="rounded-[10px]" /></div>
              </div>
              <div className="space-y-1.5"><Label>{t("Beschreibung", "Description")}</Label>
                <Textarea rows={2} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="rounded-[10px]" /></div>
              <div className="space-y-1.5">
                <Label>{t("Rollen (leer = alle)", "Roles (empty = all)")}</Label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((r) => {
                    const active = (edit.assigned_roles || []).includes(r);
                    return (
                      <button key={r} type="button" onClick={() => setEdit({ ...edit, assigned_roles: active ? edit.assigned_roles.filter((x) => x !== r) : [...(edit.assigned_roles || []), r] })}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? "bg-[#0B1220] text-white border-transparent" : "border-border hover:bg-muted"}`}>
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("Punkte", "Items")}</Label>
                {(edit.items || []).map((item, i) => (
                  <div key={item.id || i} className="flex items-center gap-2">
                    <Input value={item.label} onChange={(e) => {
                      const list = [...edit.items];
                      list[i] = { ...item, label: e.target.value };
                      setEdit({ ...edit, items: list });
                    }} className="rounded-[8px] h-8 text-sm" placeholder={t("Checklistenpunkt", "Checklist item")} data-testid={`checklist-item-input-${i}`} />
                    <label className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                      <Checkbox checked={item.required !== false} onCheckedChange={(c) => {
                        const list = [...edit.items];
                        list[i] = { ...item, required: !!c };
                        setEdit({ ...edit, items: list });
                      }} /> Pflicht
                    </label>
                    <label className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                      <Checkbox checked={!!item.needs_evidence} onCheckedChange={(c) => {
                        const list = [...edit.items];
                        list[i] = { ...item, needs_evidence: !!c };
                        setEdit({ ...edit, items: list });
                      }} /> {t("Nachweis", "Evidence")}
                    </label>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive shrink-0" onClick={() => setEdit({ ...edit, items: edit.items.filter((_, x) => x !== i) })}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
                <Button type="button" variant="ghost" size="sm" className="rounded-[8px] text-xs" onClick={() => setEdit({ ...edit, items: [...(edit.items || []), { id: uid(), label: "", required: true, needs_evidence: false }] })} data-testid="checklist-add-item">
                  <Plus className="h-3 w-3 mr-1" /> {t("Punkt hinzuf\u00fcgen", "Add item")}
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button className="rounded-[10px] bg-[#0B1220]" onClick={save} data-testid="checklist-save-button">{t("Speichern", "Save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!subs} onOpenChange={(o) => !o && setSubs(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{t("Einreichungen", "Submissions")}: {subs?.cl.title}</DialogTitle></DialogHeader>
          {subs?.list.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("Noch keine Einreichungen.", "No submissions yet.")}</p>
          ) : (
            <div className="divide-y divide-border max-h-[50vh] overflow-y-auto">
              {subs?.list.map((s) => (
                <div key={s.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <span>{s.user_name}</span>
                  <Badge variant={s.status === "completed" ? "default" : "outline"} className={`rounded-full text-[10px] ${s.status === "completed" ? "bg-emerald-600" : ""}`}>
                    {s.status === "completed" ? t("Abgeschlossen", "Completed") : t("In Bearbeitung", "In progress")}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =============== Page =============== */
export default function AdminDocuments() {
  const { t } = useLang();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/docs/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  if (!meta) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96 rounded-[16px]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-documents-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Dokumente & Vorlagen", "Documents & Templates")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("Eleven Compliance Center \u2013 Vorlagen, Textbausteine, Dokumente, Versionen und Checklisten verwalten", "Eleven Compliance Center \u2013 manage templates, text blocks, documents, versions and checklists")}</p>
      </div>
      <Tabs defaultValue="vorlagen">
        <TabsList className="rounded-[12px] flex-wrap h-auto">
          <TabsTrigger value="vorlagen" className="rounded-[10px]" data-testid="tab-vorlagen"><FileText className="h-3.5 w-3.5 mr-1.5" /> {t("Vorlagen", "Templates")}</TabsTrigger>
          <TabsTrigger value="bausteine" className="rounded-[10px]" data-testid="tab-bausteine"><Blocks className="h-3.5 w-3.5 mr-1.5" /> {t("Textbausteine", "Text blocks")}</TabsTrigger>
          <TabsTrigger value="dokumente" className="rounded-[10px]" data-testid="tab-dokumente"><Download className="h-3.5 w-3.5 mr-1.5" /> {t("Dokumente", "Documents")}</TabsTrigger>
          <TabsTrigger value="checklisten" className="rounded-[10px]" data-testid="tab-checklisten"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> {t("Checklisten", "Checklists")}</TabsTrigger>
        </TabsList>
        <TabsContent value="vorlagen" className="mt-5"><TemplatesTab meta={meta} /></TabsContent>
        <TabsContent value="bausteine" className="mt-5"><BlocksTab meta={meta} /></TabsContent>
        <TabsContent value="dokumente" className="mt-5"><DocumentsTab meta={meta} /></TabsContent>
        <TabsContent value="checklisten" className="mt-5"><ChecklistsTab meta={meta} /></TabsContent>
      </Tabs>
    </div>
  );
}
